import {
  Modal,
  Input,
  Textarea,
  NumberInput,
  Divider,
  Button,
  Stack,
  Text,
  Group,
  Flex,
  Box,
  Select,
  Tooltip,
  ActionIcon,
  useMantineTheme,
  rem,
  TagsInput,
} from "@mantine/core";
import { DateInput, DatesProvider } from "@mantine/dates";
import dayjs from "dayjs";
import React, { Dispatch, SetStateAction, useEffect, useState } from "react";
import { IconCalendar } from "../../../shared/svg/IconCalendar";
import useProfileStore from "../../../store/useProfileStore";
import { LearningPlan } from "../../../types/api";
import { useTranslation } from "react-i18next";
import { IconSettings } from "../../../shared/svg/IconSettings";
import CalendarViewMode from "./CalendarViewMode";
import { useMediaQuery } from "@mantine/hooks";

const statusMapping: Record<"planned" | "in_progress" | "completed", string> = {
  planned: "Planned",
  in_progress: "In Progress",
  completed: "Completed",
};

interface CalendarModalProps {
  opened: boolean;
  onClose: () => void;
  data: LearningPlan;
  onChange: (updated: LearningPlan) => void;
  onSave: () => void;
  onDelete?: (id: number) => void;
  isEditing?: boolean;
  errors: {
    title?: string;
  };
  setFormErrors: Dispatch<
    SetStateAction<{
      title?: string;
    }>
  >;
}

export const CalendarModal: React.FC<CalendarModalProps> = ({
  opened,
  onClose,
  data,
  onChange,
  onSave,
  onDelete,
  isEditing = false,
  errors,
  setFormErrors,
}) => {
  const { profileLanguage } = useProfileStore();
  const { t } = useTranslation();
  const [isDeleteOpen, setIsDeleteOpen] = useState<boolean>(false);
  const [isEditMode, setIsEditMode] = useState(!isEditing); // edit mode true only for create
  const theme = useMantineTheme();
  const isMobile = useMediaQuery("(pointer: coarse)");

  const commonStyles: React.CSSProperties = {
    backgroundColor: theme.colors.gray[1],
    color: theme.colors.dark[7],
    fontSize: rem(14),
    padding: `${rem(4)} ${rem(8)}`,
    borderRadius: rem(4),
    boxShadow: theme.shadows.sm,
    maxWidth: isMobile ? rem(300) : rem(500),
    wordWrap: "break-word" as "break-word",
    whiteSpace: "normal",
  };

  useEffect(() => {
    if (isEditing) {
      setIsEditMode(false);
    } else {
      setIsEditMode(true);
    }
  }, [opened, isEditing]);

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      zIndex={301}
      title={
        <Flex align="center" justify="space-between" gap={6}>
          <Text fw={600}>
            {isEditing ? t("edit_calendar") : t("new_calendar")}
          </Text>
          {isEditing && !isEditMode && (
            <Tooltip
              zIndex={301}
              label={t("edit_calendar_tooltip")}
              withArrow
              transitionProps={{ duration: 100, transition: "fade" }}
              arrowSize={6}
              arrowOffset={8}
              position="top"
              offset={6}
              styles={{
                tooltip: commonStyles,
                arrow: { backgroundColor: theme.colors.gray[1] },
              }}
            >
              <ActionIcon
                variant="subtle"
                onClick={() => setIsEditMode(!isEditMode)}
              >
                <IconSettings />
              </ActionIcon>
            </Tooltip>
          )}
        </Flex>
      }
      centered
      size="lg"
    >
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        {isEditMode ? (
          <>
            <Input.Wrapper label={t("title")} required error={errors.title}>
              <Input
                data-autofocus
                value={data.title}
                onChange={(e) => {
                  onChange({ ...data, title: e.currentTarget.value });
                  if (errors.title) setFormErrors({});
                }}
                maxLength={250}
              />
            </Input.Wrapper>

            <Textarea
              label={t("description")}
              value={data.description ?? ""}
              onChange={(e) =>
                onChange({ ...data, description: e.currentTarget.value })
              }
              autosize
              minRows={1}
            />

            <Select
              comboboxProps={{
                zIndex: 302,
                withinPortal: true,
              }}
              label={t("form.status")}
              value={data.status}
              onChange={(e) => {
                onChange({ ...data, status: e ? e : undefined });
              }}
              data={Object.keys(statusMapping).map((key) => ({
                value: key,
                label: t(`status.${key}`),
              }))}
              w={"100%"}
            />

            <Flex gap={12} align="flex-end">
              <Box style={{ flex: 1 }}>
                <DatesProvider
                  settings={{ locale: Object.keys(profileLanguage!)[0] }}
                >
                  <DateInput
                    label={t("deadline")}
                    popoverProps={{
                      zIndex: 302,
                    }}
                    placeholder={t("choose_date")}
                    value={data.deadline ? new Date(data.deadline) : null}
                    onChange={(date) =>
                      onChange({
                        ...data,
                        deadline: date ? dayjs(date).format("YYYY-MM-DD") : "",
                      })
                    }
                    clearable
                    leftSection={<IconCalendar />}
                  />
                </DatesProvider>
              </Box>

              <Box style={{ flex: 1 }}>
                <NumberInput
                  label={t("estimated_total_hours")}
                  value={data.estimated_total_hours ?? undefined}
                  onChange={(val) =>
                    onChange({
                      ...data,
                      estimated_total_hours: Number(val) ?? 0,
                    })
                  }
                  min={0}
                />
              </Box>
            </Flex>

            <TagsInput
              label={t("skills_to_gain")}
              value={
                Array.isArray(data.skills_to_gain)
                  ? data.skills_to_gain
                  : typeof data.skills_to_gain === "string" &&
                    data.skills_to_gain.trim() !== ""
                  ? data.skills_to_gain.split(",").map((s) => s.trim())
                  : []
              }
              onChange={(newTags) =>
                onChange({ ...data, skills_to_gain: newTags })
              }
              placeholder={t("add_skill")}
              clearable
            />

            <Textarea
              label={t("motivation_note")}
              value={data.motivation_note ?? ""}
              onChange={(e) =>
                onChange({ ...data, motivation_note: e.currentTarget.value })
              }
              autosize
              minRows={1}
            />

            <Textarea
              label={t("coaching_notes")}
              value={data.coaching_notes ?? ""}
              onChange={(e) =>
                onChange({ ...data, coaching_notes: e.currentTarget.value })
              }
              autosize
              minRows={1}
            />
          </>
        ) : (
          <CalendarViewMode data={data} t={t} />
        )}

        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            marginTop: 16,
          }}
        >
          {isEditing && onDelete && (
            <Button
              variant="outline"
              color="red"
              onClick={() => setIsDeleteOpen(true)}
            >
              {t("delete")}
            </Button>
          )}
          {isEditMode && (
            <Button onClick={onSave}>
              {isEditing ? t("save_changes") : t("create")}
            </Button>
          )}
        </div>
      </div>

      {/* 🔶 Confirm delete modal */}
      <Modal
        opened={isDeleteOpen}
        onClose={() => setIsDeleteOpen(false)}
        size="md"
        padding={20}
        centered
        zIndex={302}
      >
        <Text style={{ fontSize: "18px" }}>{t("delete_calendar_title")}</Text>
        <Group mt="30px" justify="flex-end">
          <Button onClick={() => setIsDeleteOpen(false)} variant="default">
            {t("cancel")}
          </Button>
          <Button
            onClick={async () => {
              if (data.id && onDelete) onDelete(data.id);
              setIsDeleteOpen(false);
            }}
            color="red"
          >
            {t("delete")}
          </Button>
        </Group>
      </Modal>
    </Modal>
  );
};
